import { useCallback, useEffect, useState, useContext } from "react";

import LoadingOverlay from "../../../../containers/LoadingOverlay/LoadingOverlay";
import ReportTableHeader from "../../../../containers/DocumentsReport/ReportTableHeader/ReportTableHeader";
import CommonPagination from "../../../../reusable-components/CommonPagination/CommonPagination";

import LegalEntityRegDoc from "./LegalEntityRegDoc/LegalEntityRegDoc";

import { loadLegalEntityRegDocs } from "../../../../services/legal-entity-reg-docs/legal-entity-reg-docs-api.service";

import useSearchOptions from "../../../../custom-hooks/useSearchOptions/useSearchOptions";

import { FT_RDIP } from "../../../../utils/constants/filter-tables-constants";

import { State } from "./interfaces";
import { Container } from "./styles";

import { ComplianceDocumentsContext } from "../ComplianceDocuments";

import { useAppSelector } from "../../../../custom-hooks/redux/redux";
import useAuthorization from "src/custom-hooks/useAuthorization/useAuthorization";
import { AUTHORIZATIONS } from "src/utils/constants/authorization-constants";
import QuickfiltersContainer from "src/containers/Quickfilters/QuickfiltersContainer/QuickfiltersContainer";
import { useTranslation } from "react-i18next";

const PAGE_SIZE = 10;
export const PAGING_BY_DOCTYPE_PAGE_SIZE = 10; // same as default on back-end

const RegistrationsDocumentsIdsPoas = () => {
  const tRP = "pages.document_reports.compliance_documents.registrations_documents";
  const { t } = useTranslation();
  const hasAccessToPage = useAuthorization([
    AUTHORIZATIONS.LegalEntityAccess.RunAccountingReports,
    AUTHORIZATIONS.LegalEntityAccess.ProcessAccounting,
  ]);

  const {
    legalEntity: { legalEntityOption, selectedLegalEntity, legalEntityQuickFilter, legalEntities },
  } = useContext(ComplianceDocumentsContext);

  const [state, setState] = useState<State>({
    loading: false,
    error: "",
    items: [],
    totalItems: 0,
    pagingByDocType: [],
    page: 0,
  });

  const handleChangePage = (page: number) => setState((state) => ({ ...state, page: page - 1 }));

  const { searchOptions, handleChangeSearchOptions } = useSearchOptions(FT_RDIP);

  const handleChangePagingByDocType = async (docTypeId: string, pageNumber: number) => {
    const leRegDoc = state.items.find(({ documentType }) => documentType?.id === docTypeId);

    const pagingByDocType = state.items.map(({ documentType, page }) => ({
      documentTypeId: documentType?.id ?? "",
      page: {
        ...page,
        ...(leRegDoc?.documentType && documentType?.id === leRegDoc?.documentType.id
          ? { pageNumber: pageNumber - 1 }
          : {}),
      },
    }));

    if (pagingByDocType) setState((state) => ({ ...state, pagingByDocType }));
  };

  const getLegalEntityRegDocs = useCallback(
    async (signal?: AbortSignal) => {
      setState((state) => ({ ...state, loading: true }));

      try {
        const { items, totalItems, error } = await loadLegalEntityRegDocs(
          legalEntityOption?.legalEntityIds ?? [],
          searchOptions,
          { pageNumber: state.page, pageSize: PAGE_SIZE },
          state.pagingByDocType,
          signal
        );

        if (!signal || !signal.aborted)
          setState((state) => ({
            ...state,
            error: error as string,
            items: !!state.pagingByDocType.length
              ? items.map((item) => ({
                  ...item,
                  page: state.pagingByDocType.find((i) => i.documentTypeId === item.documentType?.id)?.page ?? {
                    pageNumber: 0,
                    pageSize: PAGING_BY_DOCTYPE_PAGE_SIZE,
                  },
                }))
              : items.map((item) => ({
                  ...item,
                  page: {
                    pageNumber: 0,
                    pageSize: PAGING_BY_DOCTYPE_PAGE_SIZE,
                  },
                })),
            totalItems,
          }));
      } catch (error) {
        if (!signal || !signal.aborted) setState((state) => ({ ...state, error: error as string }));
      } finally {
        if (!signal || !signal.aborted) setState((state) => ({ ...state, loading: false }));
      }
    },
    [legalEntityOption?.legalEntityIds, searchOptions, state.page, state.pagingByDocType]
  );

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    const abortController = new AbortController();

    timer = setTimeout(() => getLegalEntityRegDocs(abortController.signal), searchOptions.query ? 500 : 0);

    return () => {
      abortController.abort();
      clearTimeout(timer);
    };
  }, [getLegalEntityRegDocs, searchOptions.query, state.page]);

  return (
    <>
      {hasAccessToPage && (
        <LoadingOverlay
          loading={state.loading}
          error={state.error}
        >
          <Container>
            <ReportTableHeader
              title={`${t(`${tRP}.tab_label`)}`}
              searchOptions={searchOptions}
              handleChangeSearchOptions={handleChangeSearchOptions}
            >
              <QuickfiltersContainer>{legalEntityQuickFilter}</QuickfiltersContainer>
            </ReportTableHeader>
            <CommonPagination
              page={state.page + 1}
              handleChangePage={handleChangePage}
              totalItems={state.totalItems}
              defaultPageSize={PAGE_SIZE}
            />
            {state.items.map((item, idx) => (
              <LegalEntityRegDoc
                key={idx}
                legalEntityRegDocPageByType={item}
                handleChangePagingByDocType={handleChangePagingByDocType}
                handleNotifyParent={(_) => getLegalEntityRegDocs()}
                currentLegalEntity={selectedLegalEntity ?? legalEntities[0]}
              />
            ))}
          </Container>
        </LoadingOverlay>
      )}
    </>
  );
};

export default RegistrationsDocumentsIdsPoas;
